// src/components/WalletPage.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import PageWrapper from "./PageWrapper";
import axiosInstance from "../axiosInstance";
import {
  WalletIcon,
  DollarSignIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  HistoryIcon,
  SendIcon,
} from "../common/Icons";
import { LanguageContext } from "../contexts/LanguageContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const translations = {
  en: {
    // General
    loadingWallet: "Loading wallet...",
    sessionExpired: "Your session has expired or is invalid. Redirecting to login...",
    networkError: "Network or server error.",
    failedToFetchUser: "Failed to fetch user data.",
    goToLogin: "Go to Login",
    myWallet: "My Wallet",
    depositBalance: "Deposit Balance",
    bonusBalance: "Bonus Balance",
    birr: "Birr",
    deposit: "Deposit",
    withdraw: "Withdraw",
    transfer: "Transfer",
    history: "History",
    error: "Error",
    
    // Deposit
    depositFunds: "Deposit Funds",
    depositAmount: "Deposit Amount",
    depositAmountPlaceholder: "Enter amount (Min: 100, Max: 100,000)",
    processingDeposit: "Initiating deposit via {bankType}...",
    depositInitiated: "Deposit initiated. Please check your phone for the payment prompt.",
    depositSuccessful: "Deposit successful! Balance updated.",
    depositFailed: "Deposit initiation failed.",
    validAmount: "Please enter a valid amount.",
    bankNotSpecified: "Payment method not specified.",
    selectPaymentMethod: "Select Payment Method",
    minDeposit: "Minimum deposit is 100 Birr",
    maxDeposit: "Maximum deposit is 100,000 Birr",
    
    // Withdraw
    withdrawCash: "Withdraw Cash",
    directWithdraw: "Direct Withdraw",
    enterAmount: "Enter amount",
    enterAmountPlaceholder: "Enter amount (Min: 100, Max: 10,000)",
    insufficientBalance: "Insufficient deposit balance.",
    processingWithdrawal: "Processing withdrawal...",
    withdrawalSuccessful: "Withdrawal request submitted successfully!",
    withdrawalInitiated: "Withdrawal initiated. Please check your transaction history.",
    withdrawalFailed: "Withdrawal failed.",
    minWithdrawal: "Minimum withdrawal is 100 Birr",
    maxWithdrawal: "Maximum withdrawal is 10,000 Birr",
    insufficientBalanceToMaintainMinimum: "Insufficient balance. A minimum of 100 Birr must remain after withdrawal.",
    
    // Transfer
    transferFunds: "Transfer Funds",
    recipientPhoneNumber: "Recipient Phone Number",
    amountToTransfer: "Amount to transfer",
    confirmTransfer: "Confirm Transfer",
    processingTransfer: "Processing transfer...",
    transferSuccessful: "Funds transferred successfully!",
    transferFailed: "Transfer failed.",
    minTransfer: "Minimum transfer is 100 Birr",
    maxTransfer: "Maximum transfer is 100 Birr",
    cannotTransferToSelf: "You cannot transfer funds to yourself.",
    
    // Transaction History
    transactionHistory: "Transaction History",
    loadingTransactions: "Loading transactions...",
    noTransactions: "No transactions yet.",
    type: "Type",
    amount: "Amount",
    status: "Status",
    date: "Date",
    bank: "Bank",
    success: "Success",
    pending: "Pending",
    failed: "Failed",
    na: "N/A",
    
    // Withdrawal Requests
    withdrawRequests: "Withdrawal Requests",
    requestId: "Request ID",
    adminNotes: "Admin Notes",
    requestedOn: "Requested On",
    approvedAt: "Approved At",
    completedAt: "Completed At",
    
    // Payment Status
    checkingPaymentStatus: "Checking payment status...",
    paymentSuccessful: "Payment successful! Adding funds to your wallet...",
    paymentFailed: "Payment failed or was cancelled.",
    checkingPayment: "Checking payment status...",
    pleaseCheckPhone: "Please check your phone for the USSD push notification",
    paymentTimeout: "Payment status check timed out. Please check your transaction history later.",
    
    // Phone validation
    invalidPhoneNumber: "Please enter a valid phone number.",
    recipientPhoneMissing: "Please enter a recipient phone number.",
    networkOrServerError: "Network or server error.",
    depositFinalized: "Deposit successfully finalized! Balance updated.",
    
    // Banks
    telebirr: "Tele Birr",
    telebirrUssd: "Tele Birr",
    cbeBirrOption: "CBE Birr",
    mPesa: "M-Pesa",
    eBirr: "E-Birr",
    amole: "Amole",
    coopay: "Coopay E-Birr",
    awashBirr: "Awash Birr",
    yaya: "Yaya Wallet",
    enatBank: "Enat Bank",
  },
  am: {
    // General
    loadingWallet: "የኪስ ቦርሳ በመጫን ላይ...",
    sessionExpired: "የእርስዎ ክፍለ ጊዜ አልቋል ወይም ልክ ያልሆነ ነው። ወደ መግቢያ በመዞር ላይ...",
    networkError: "የአውታረ መረብ ወይም የአገልጋይ ስህተት።",
    failedToFetchUser: "የተጠቃሚ ውሂብ ማግኘት አልተሳካም።",
    goToLogin: "ወደ መግቢያ ይሂዱ",
    myWallet: "የእኔ የኪስ ቦርሳ",
    depositBalance: "የተቀማጭ ቀሪ ሂሳብ",
    bonusBalance: "የጉርሻ ቀሪ ሂሳብ",
    birr: "ብር",
    deposit: "ገንዘብ ያስቀምጡ",
    withdraw: "ገንዘብ ያውጡ",
    transfer: "ገንዘብ ያስተላልፉ",
    history: "ታሪክ",
    error: "ስህተት",
    
    // Deposit
    depositFunds: "ገንዘብ ያስቀምጡ",
    depositAmount: "የተቀማጭ ገንዘብ መጠን",
    depositAmountPlaceholder: "መጠን ያስገቡ (አነስተኛ: 100, ከፍተኛ: 100,000)",
    processingDeposit: "በ{bankType} በኩል የተቀማጭ ገንዘብ በመጀመር ላይ...",
    depositInitiated: "ተቀማጭ ገንዘብ ተጀምሯል። እባክዎ ስልክዎን ያረጋግጡ።",
    depositSuccessful: "ተቀማጭ ገንዘብ ተሳክቷል! ቀሪ ሂሳብ ተዘምኗል።",
    depositFailed: "ተቀማጭ ገንዘብ ማስጀመር አልተሳካም።",
    validAmount: "እባክዎ ትክክለኛ መጠን ያስገቡ።",
    bankNotSpecified: "የክፍያ ዘዴ አልተገለጸም።",
    selectPaymentMethod: "የክፍያ ዘዴ ይምረጡ",
    minDeposit: "አነስተኛ የማስገቢያ መጠን 100 ብር ነው",
    maxDeposit: "ከፍተኛ የማስገቢያ መጠን 100,000 ብር ነው",
    
    // Withdraw
    withdrawCash: "ገንዘብ ያውጡ",
    directWithdraw: "ቀጥተኛ ገንዘብ ማውጣት",
    enterAmount: "መጠን ያስገቡ",
    enterAmountPlaceholder: "መጠን ያስገቡ (አነስተኛ: 100, ከፍተኛ: 10,000)",
    insufficientBalance: "በቂ ያልሆነ የተቀማጭ ቀሪ ሂሳብ።",
    processingWithdrawal: "ገንዘብ ማውጣት በመስራት ላይ...",
    withdrawalSuccessful: "የገንዘብ ማውጣት ጥያቄ ተልኳል!",
    withdrawalInitiated: "ገንዘብ ማውጣት ተጀምሯል። እባክዎ የግብይት ታሪክዎን ያረጋግጡ።",
    withdrawalFailed: "ገንዘብ ማውጣት አልተሳካም።",
    minWithdrawal: "አነስተኛ የማውጣት መጠን 100 ብር ነው",
    maxWithdrawal: "ከፍተኛ የማውጣት መጠን 10,000 ብር ነው",
    insufficientBalanceToMaintainMinimum: "በቂ ያልሆነ ቀሪ ሂሳብ። ገንዘብ ካወጡ በኋላ ቢያንስ 100 ብር መቆየት አለበት።",
    
    // Transfer
    transferFunds: "ገንዘብ ያስተላልፉ",
    recipientPhoneNumber: "የተቀባይ ስልክ ቁጥር",
    amountToTransfer: "የማስተላለፊያ መጠን",
    confirmTransfer: "ማስተላለፍ ያረጋግጡ",
    processingTransfer: "ማስተላለፍ በመስራት ላይ...",
    transferSuccessful: "ገንዘቦች በተሳካ ሁኔታ ተላልፈዋል!",
    transferFailed: "ማስተላለፍ አልተሳካም።",
    minTransfer: "አነስተኛ የዝውውር መጠን 100 ብር ነው",
    maxTransfer: "ከፍተኛ የዝውውር መጠን 100 ብር ነው",
    cannotTransferToSelf: "ወደ ራስዎ ገንዘብ ማስተላለፍ አይችሉም።",
    
    // Transaction History
    transactionHistory: "የግብይት ታሪክ",
    loadingTransactions: "ግብይቶችን በመጫን ላይ...",
    noTransactions: "እስካሁን ምንም ግብይቶች የሉም።",
    type: "አይነት",
    amount: "መጠን",
    status: "ሁኔታ",
    date: "ቀን",
    bank: "ባንክ",
    success: "ተሳክቷል",
    pending: "በሂደት ላይ",
    failed: "አልተሳካም",
    na: "የለም",
    
    // Withdrawal Requests
    withdrawRequests: "የገንዘብ ማውጫ ጥያቄዎች",
    requestId: "የጥያቄ መለያ",
    adminNotes: "የአስተዳዳሪ ማስታወሻ",
    requestedOn: "የተጠየቀበት ቀን",
    approvedAt: "የጸደቀበት ቀን",
    completedAt: "የተጠናቀቀበት ቀን",
    
    // Payment Status
    checkingPaymentStatus: "የክፍያ ሁኔታ በመፈተሽ ላይ...",
    paymentSuccessful: "ክፍያ ተሳክቷል! ገንዘብ ወደ ሂሳብዎ እየጨመረ ነው...",
    paymentFailed: "ክፍያ አልተሳካም ወይም ተሰርዟል።",
    checkingPayment: "የክፍያ ሁኔታ በመፈተሽ ላይ...",
    pleaseCheckPhone: "እባክዎ ለ USSD ማሳወቂያ ስልክዎን ያረጋግጡ",
    paymentTimeout: "የክፍያ ሁኔታ ምርመራ ጊዜው አልፏል። እባክዎ በኋላ የግብይት ታሪክዎን ያረጋግጡ።",
    
    // Phone validation
    invalidPhoneNumber: "እባክዎ ትክክለኛ ስልክ ቁጥር ያስገቡ።",
    recipientPhoneMissing: "እባክዎ የተቀባይ ስልክ ቁጥር ያስገቡ።",
    networkOrServerError: "የአውታረ መረብ ወይም የአገልጋይ ስህተት።",
    depositFinalized: "ተቀማጭ ገንዘብ በተሳካ ሁኔታ ተጠናቋል! ቀሪ ሂሳብ ተዘምኗል።",
    
    // Banks
    telebirr: "ቴሌ ብር",
    telebirrUssd: "ቴሌ ብር",
    cbeBirrOption: "ሲቢኢ ብር",
    mPesa: "ኤም-ፔሳ",
    eBirr: "ኢ-ብር",
    amole: "አሞሌ",
    coopay: "ኮኦፔይ ኢ-ብር",
    awashBirr: "አዋሽ ብር",
    yaya: "ያያ ዎሌት",
    enatBank: "እናት ባንክ",
  },
  ti: {
    // General
    loadingWallet: "ሒሳብ ገንዘብ ይጽዕን ኣሎ...",
    sessionExpired: "ናይ'ዚ ስራሕ ግዜኹም ወዲኡ ወይ ድማ ቅኑዕ ኣይኮነን። ናብ ምእታው ይምለስ ኣሎ...",
    networkError: "ጌጋ መርበብ ወይ ሰርቨር።",
    failedToFetchUser: "ናይ ተጠቃሚ ሓበሬታ ምምጻእ ኣይከኣለን።",
    goToLogin: "ናብ ምእታው ይኸይድ",
    myWallet: "ናይ ብሕተይ ገንዘብ መክዘኒ",
    depositBalance: "ተቀማጢ ገንዘብ",
    bonusBalance: "ተወሳኺ ገንዘብ",
    birr: "ብር",
    deposit: "ገንዘብ ኣዋድቅ",
    withdraw: "ገንዘብ ኣውጽእ",
    transfer: "ገንዘብ ኣመሓላልፍ",
    history: "ታሪኽ",
    error: "ጌጋ",
    
    // Deposit
    depositFunds: "ገንዘብ ኣዋድቅ",
    depositAmount: "መጠን ተቀማጢ ገንዘብ",
    depositAmountPlaceholder: "መጠን ኣእትዉ (ዝተሓተ: 100, ዝለዓለ: 100,000)",
    processingDeposit: "ተቀማጢ ገንዘብ ብ{bankType} ይጅምር ኣሎ...",
    depositInitiated: "ተቀማጢ ገንዘብ ተጀሚሩ። በጃኹም ስልክኹም ረኣኹ።",
    depositSuccessful: "ተቀማጢ ገንዘብ ተዓዊቱ! ሒሳብ ተሓዲሱ።",
    depositFailed: "ተቀማጢ ገንዘብ ምጅማር ኣይተዓወተን።",
    validAmount: "በጃኹም ቅኑዕ መጠን ኣእትዉ።",
    bankNotSpecified: "ናይ ክፍሊት ኣገባብ ኣይተወሰነን።",
    selectPaymentMethod: "ናይ ክፍሊት ኣገባብ ምረጽ",
    minDeposit: "ዝተሓተ ተቀማጢ 100 ብር እዩ",
    maxDeposit: "ዝለዓለ ተቀማጢ 100,000 ብር እዩ",
    
    // Withdraw
    withdrawCash: "ገንዘብ ኣውጽእ",
    directWithdraw: "ቀጥታዊ ምውጻእ ገንዘብ",
    enterAmount: "መጠን ኣእትዉ",
    enterAmountPlaceholder: "መጠን ኣእትዉ (ዝተሓተ: 100, ዝለዓለ: 10,000)",
    insufficientBalance: "ዝተቀመጠ ገንዘብ ኣይበቅዕን።",
    processingWithdrawal: "ገንዘብ ምውጻእ ይካየድ ኣሎ...",
    withdrawalSuccessful: "ናይ ገንዘብ ምውጻእ ልማኖ ተላእኩ!",
    withdrawalInitiated: "ገንዘብ ምውጻእ ተጀሚሩ። በጃኹም ናይ ግብይት ታሪኽኩም ኣረጋግጽ።",
    withdrawalFailed: "ገንዘብ ምውጻእ ኣይተዓወተን።",
    minWithdrawal: "ዝተሓተ ምውጻእ 100 ብር እዩ",
    maxWithdrawal: "ዝለዓለ ምውጻእ 10,000 ብር እዩ",
    insufficientBalanceToMaintainMinimum: "ዝተሓተ ሒሳብ ንምውጻእ ኣይበቅዕን። ገንዘብ ካወጻእኩም ድሕሪኡ ቢያንስ 100 ብር ክተርፍ ኣለዎ።",
    
    // Transfer
    transferFunds: "ገንዘብ ኣመሓላልፍ",
    recipientPhoneNumber: "ቁጽሪ ስልኪ ተቀባሊ",
    amountToTransfer: "መጠን ንምምሕልላፍ",
    confirmTransfer: "ምምሕልላፍ ኣረጋግጽ",
    processingTransfer: "ምምሕልላፍ ይካየድ ኣሎ...",
    transferSuccessful: "ገንዘብ ብዓወት ተመሓላሊፉ!",
    transferFailed: "ምምሕልላፍ ኣይተዓወተን።",
    minTransfer: "ዝተሓተ ምምሕልላፍ 100 ብር እዩ",
    maxTransfer: "ዝለዓለ ምምሕልላፍ 100 ብር እዩ",
    cannotTransferToSelf: "ናብ ርእስኹም ምምሕልላፍ ኣይትኽእሉን።",
    
    // Transaction History
    transactionHistory: "ታሪኽ ግብይት",
    loadingTransactions: "ግብይታት ይጽዓኑ ኣለዉ...",
    noTransactions: "ክሳብ ሕጂ ግብይታት የለን።",
    type: "ዓይነት",
    amount: "መጠን",
    status: "ኩነታት",
    date: "ዕለት",
    bank: "ባንክ",
    success: "ዓወት",
    pending: "ይካየድ ኣሎ",
    failed: "ኣይተዓወተን",
    na: "የልቦን",
    
    // Withdrawal Requests
    withdrawRequests: "ልማኖት ምውጻእ ገንዘብ",
    requestId: "ናይ ልማኖ መለለዪ",
    adminNotes: "ማስታወሻ ኣስተዳዳሪ",
    requestedOn: "ዝተለመነሉ ዕለት",
    approvedAt: "ዝፀደቀሉ ዕለት",
    completedAt: "ዝተዛዘመሉ ዕለት",
    
    // Payment Status
    checkingPaymentStatus: "ኩነታት ክፍሊት ይምርመር ኣሎ...",
    paymentSuccessful: "ክፍሊት ተዓዊቱ! ገንዘብ ናብ ሒሳብኩም ይውሰኽ ኣሎ...",
    paymentFailed: "ክፍሊት ኣይተዓወተን ወይ ተሰሪዙ።",
    checkingPayment: "ኩነታት ክፍሊት ይምርመር ኣሎ...",
    pleaseCheckPhone: "በጃኹም ን USSD ምዝራብ ስልክኹም ረኣኹ",
    paymentTimeout: "ኩነታት ክፍሊት ምርመራ ግዜኡ ሓሊፉ። በጃኹም ድሕሪ ግዜ ናይ ግብይት ታሪኽኩም ረኣኹ።",
    
    // Phone validation
    invalidPhoneNumber: "በጃኹም ቅኑዕ ቁጽሪ ስልኪ ኣእትዉ።",
    recipientPhoneMissing: "በጃኹም ቁጽሪ ስልኪ ተቀባሊ ኣእትዉ።",
    networkOrServerError: "ጌጋ መርበብ ወይ ሰርቨር።",
    depositFinalized: "ተቀማጢ ገንዘብ ብዓወት ተዛዚሙ! ሒሳብ ተሓዲሱ።",
    
    // Banks
    telebirr: "ተሌ ብር",
    telebirrUssd: "ተሌ ብር",
    cbeBirrOption: "ሲቢኢ ብር",
    mPesa: "ኤም-ፔሳ",
    eBirr: "ኢ-ብር",
    amole: "አሞሌ",
    coopay: "ኮኦፔይ ኢ-ብር",
    awashBirr: "አዋሽ ብር",
    yaya: "ያያ ዎሌት",
    enatBank: "እናት ባንክ",
  },
  or: {
    // General
    loadingWallet: "Boorsaa guutumaa jira...",
    sessionExpired: "Yeroo keessan xumureera ykn hin hojjetu. Gara seensaatti deebi'aa...",
    networkError: "Dogoggora networkii ykn serverii.",
    failedToFetchUser: "Odeeffannoo fayyadamaa argachuu hindandeenye.",
    goToLogin: "Gara seensaatti deebi'i",
    myWallet: "Boorsa koo",
    depositBalance: "Madaallii qusannaa",
    bonusBalance: "Madaallii badhaasaa",
    birr: "Birr",
    deposit: "Qusadhu",
    withdraw: "Baasi",
    transfer: "Dabarsi",
    history: "Seenaa",
    error: "Dogoggora",
    
    // Deposit
    depositFunds: "Maallaqa qusadhu",
    depositAmount: "Baay'ina qusannaa",
    depositAmountPlaceholder: "Baay'ina galchi (Xinnoo: 100, Ol ta'e: 100,000)",
    processingDeposit: "Qusannaa {bankType} irraan eegalaa jira...",
    depositInitiated: "Qusannaan eegaleera. Maaloo bilbilaa keessan mirkaneessaa.",
    depositSuccessful: "Qusannaan milkaa'eera! Madaallii haaromsameera.",
    depositFailed: "Qusannaan eegaluu hin milkoofne.",
    validAmount: "Maaloo baay'ina sirrii galchi.",
    bankNotSpecified: "Gosni kaffaltii hin murtoofne.",
    selectPaymentMethod: "Gosni Kaffaltii Filadhu",
    minDeposit: "Qusannaa xiqqaan 100 Birr",
    maxDeposit: "Qusannaa guddaan 100,000 Birr",
    
    // Withdraw
    withdrawCash: "Maallaqa baasi",
    directWithdraw: "Baasii kallattii",
    enterAmount: "Baay'ina galchi",
    enterAmountPlaceholder: "Baay'ina galchi (Xinnoo: 100, Ol ta'e: 10,000)",
    insufficientBalance: "Madaallii qusannaa hin ga'u.",
    processingWithdrawal: "Baasii raawwataa jira...",
    withdrawalSuccessful: "Gaafannaan baasii milkaa'inaan ergame!",
    withdrawalInitiated: "Baasiin eegaleera. Maaloo seenaa walii galtee keessan mirkaneessaa.",
    withdrawalFailed: "Baasiin hin milkoofne.",
    minWithdrawal: "Baasii xiqqaan 100 Birr",
    maxWithdrawal: "Baasii guddaan 10,000 Birr",
    insufficientBalanceToMaintainMinimum: "Madaallii gahaa hin qabu. Baasii booda Birr 100 xiqqaan hafuu qaba.",
    
    // Transfer
    transferFunds: "Maallaqa dabarsi",
    recipientPhoneNumber: "Lakkoofsa Bilbilaa fudhataa",
    amountToTransfer: "Baay'ina dabarsuuf",
    confirmTransfer: "Dabarsa mirkaneessi",
    processingTransfer: "Dabarsa raawwataa jira...",
    transferSuccessful: "Maallaqni milkaa'inaan dabarfameera!",
    transferFailed: "Dabarsi hin milkoofne.",
    minTransfer: "Dabarsi xiqqaan 100 Birr",
    maxTransfer: "Dabarsi guddaan 100 Birr",
    cannotTransferToSelf: "Ofii keessanitti dabarsuu hindandeenye.",
    
    // Transaction History
    transactionHistory: "Seenaa Walii galtee",
    loadingTransactions: "Walii galteewwan fe'amaa jiru...",
    noTransactions: "Ammas walii galteewwan hin jiran.",
    type: "Gosa",
    amount: "Baay'ina",
    status: "Haala",
    date: "Guyyaa",
    bank: "Baankii",
    success: "Milkaa'ina",
    pending: "Eegamaa",
    failed: "Hin milkoofne",
    na: "Hin jiru",
    
    // Withdrawal Requests
    withdrawRequests: "Gaafannoon Baasii",
    requestId: "ID Gaafannaa",
    adminNotes: "Hubannoo Admin",
    requestedOn: "Guyyaa Gaafatame",
    approvedAt: "Guyyaa Mirkaneeffame",
    completedAt: "Guyyaa Xumurame",
    
    // Payment Status
    checkingPaymentStatus: "Haala kaffaltii mirkaneessaa jira...",
    paymentSuccessful: "Kaffaltiin milkaa'eera! Maallaqni madaallii keessanitti dabalamaa jira...",
    paymentFailed: "Kaffaltiin hin milkoofne ykn hafameera.",
    checkingPayment: "Haala kaffaltii mirkaneessaa jira...",
    pleaseCheckPhone: "Maaloo bilbilaa keessan USSD'f mirkaneessaa",
    paymentTimeout: "Yeroon mirkaneessa kaffaltii darbeera. Maaloo booda seenaa walii galtee keessan ilaalaa.",
    
    // Phone validation
    invalidPhoneNumber: "Maaloo lakkoofsa bilbilaa sirrii galchi.",
    recipientPhoneMissing: "Maaloo lakkoofsa bilbilaa fudhataa galchi.",
    networkOrServerError: "Dogoggora networkii ykn serverii.",
    depositFinalized: "Qusannaan milkaa'inaan xumurameera! Madaallii haaromsameera.",
    
    // Banks
    telebirr: "Tele Birr",
    telebirrUssd: "Tele Birr",
    cbeBirrOption: "CBE Birr",
    mPesa: "M-Pesa",
    eBirr: "E-Birr",
    amole: "Amole",
    coopay: "Coopay E-Birr",
    awashBirr: "Awash Birr",
    yaya: "Yaya Wallet",
    enatBank: "Enat Bank",
  },
  so: {
    // General
    loadingWallet: "Boorsada ku shubanayaa...",
    sessionExpired: "Waqtigaaga wuu dhammaaday ama waa mid aan sax ahayn. Loogu gudbinayaa galitaanka...",
    networkError: "Khalad shabakad ama server.",
    failedToFetchUser: "Waa ku guuldareystay soo saarista xogta isticmaalaha.",
    goToLogin: "Tag Galitaanka",
    myWallet: "Boorsadayda",
    depositBalance: "Haraaga Dhegista",
    bonusBalance: "Haraaga Abaalmarinta",
    birr: "Birr",
    deposit: "Dheji",
    withdraw: "Soo Saar",
    transfer: "Wareeji",
    history: "Taariikhda",
    error: "Khalad",
    
    // Deposit
    depositFunds: "Dheji Lacag",
    depositAmount: "Qadarka Dhegista",
    depositAmountPlaceholder: "Geli qadarka (Ugu yaraan: 100, Ugu badaan: 100,000)",
    processingDeposit: "Dhegista lagu bilaabayo {bankType}...",
    depositInitiated: "Dhegista waa la bilaabay. Fadlan telefoonkaaga ka hubi.",
    depositSuccessful: "Dhegistii guulaysatay! Haraagii waa la cusboonaysiiyay.",
    depositFailed: "Bilaabista dhegistu way guuldareysatay.",
    validAmount: "Fadlan geli qadar sax ah.",
    bankNotSpecified: "Habka lacag bixintu lama cayimin.",
    selectPaymentMethod: "Habka Lacag Bixinta",
    minDeposit: "Dhegista ugu yaraan waa 100 Birr",
    maxDeposit: "Dhegista ugu badaan waa 100,000 Birr",
    
    // Withdraw
    withdrawCash: "Soo Saar Lacag",
    directWithdraw: "Soo Saar Toos",
    enterAmount: "Gali Qadarka",
    enterAmountPlaceholder: "Geli qadarka (Ugu yaraan: 100, Ugu badaan: 10,000)",
    insufficientBalance: "Haraagii dhegista kuma filna.",
    processingWithdrawal: "Soo saarista lacagta waa la wadayaa...",
    withdrawalSuccessful: "Codsiga soo saarista lacagta ayaa si guul leh loo diray!",
    withdrawalInitiated: "Soo saaristii lacagta waa la bilaabay. Fadlan taariikhda macaamilkaaga ka hubi.",
    withdrawalFailed: "Soo saaristii lacagta waa guuldareysatay.",
    minWithdrawal: "Soo saarista ugu yaraan waa 100 Birr",
    maxWithdrawal: "Soo saarista ugu badaan waa 10,000 Birr",
    insufficientBalanceToMaintainMinimum: "Haraag kuma filna. Waa in ugu yaraan 100 Birr ka hadho soo saarista ka dib.",
    
    // Transfer
    transferFunds: "Wareeji Lacag",
    recipientPhoneNumber: "Lambarka Telefoonka Qaataha",
    amountToTransfer: "Qadarka la wareejinayo",
    confirmTransfer: "Xaqiiji Wareejinta",
    processingTransfer: "Wareejinta lacagta waa la wadayaa...",
    transferSuccessful: "Lacagta si guul leh waa loo wareejiyay!",
    transferFailed: "Wareejintii waa guuldareysatay.",
    minTransfer: "Wareejinta ugu yaraan waa 100 Birr",
    maxTransfer: "Wareejinta ugu badaan waa 100 Birr",
    cannotTransferToSelf: "Isma wareejin karto lacag.",
    
    // Transaction History
    transactionHistory: "Taariikhda Macaamilka",
    loadingTransactions: "Macaamilka la soo shubanayaa...",
    noTransactions: "Macaamil weli ma jiro.",
    type: "Nooca",
    amount: "Qadarka",
    status: "Xaaladda",
    date: "Taariikhda",
    bank: "Baankiga",
    success: "Guulaystay",
    pending: "Sugaya",
    failed: "Guuldareeyay",
    na: "Ma jirto",
    
    // Withdrawal Requests
    withdrawRequests: "Codsasyada Soo Saarista",
    requestId: "Aqoonsiga Codsiga",
    adminNotes: "Qoraalada Maamulaha",
    requestedOn: "Taariikhda La Codsaday",
    approvedAt: "Taariikhda La Ansixiyay",
    completedAt: "Taariikhda La Dhammaystiray",
    
    // Payment Status
    checkingPaymentStatus: "Xaaladda lacag bixinta waa la hubinayaa...",
    paymentSuccessful: "Lacag bixintii guulaysatay! Lacagta lagu darayaa boorsadaada...",
    paymentFailed: "Lacag bixintii waa guuldareysatay ama waa la joojiyay.",
    checkingPayment: "Xaaladda lacag bixinta waa la hubinayaa...",
    pleaseCheckPhone: "Fadlan telefoonkaaga ka hubi ogeysiiska USSD",
    paymentTimeout: "Waqtiga hubinta lacag bixinta waa dhammaaday. Fadlan taariikhda macaamilka ka dib ku soo noqo.",
    
    // Phone validation
    invalidPhoneNumber: "Fadlan geli lambar telefoon oo sax ah.",
    recipientPhoneMissing: "Fadlan geli lambarka telefoonka qaataha.",
    networkOrServerError: "Khalad shabakad ama server.",
    depositFinalized: "Dhegistii si guul leh waa loo dhammaystiray! Haraagii waa la cusboonaysiiyay.",
    
    // Banks
    telebirr: "Tele Birr",
    telebirrUssd: "Tele Birr",
    cbeBirrOption: "CBE Birr",
    mPesa: "M-Pesa",
    eBirr: "E-Birr",
    amole: "Amole",
    coopay: "Coopay E-Birr",
    awashBirr: "Awash Birr",
    yaya: "Yaya Wallet",
    enatBank: "Enat Bank",
  },
};

const WalletPage = ({ setCurrentPage, ...props }) => {
  const { language } = useContext(LanguageContext);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const token = localStorage.getItem("token");

  const MIN_DEPOSIT = 100;
  const MAX_DEPOSIT = 100000;
  const MIN_WITHDRAWAL = 100;
  const MAX_WITHDRAWAL = 10000;
  const MIN_TRANSFER = 100;
  const MAX_TRANSFER = 100;
  const MIN_BALANCE_AFTER_WITHDRAWAL = 100;

  const [depositAmount, setDepositAmount] = useState("");
  const [selectedDepositBank, setSelectedDepositBank] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [selectedWithdrawBank, setSelectedWithdrawBank] = useState("");
  const [transferPhoneNumber, setTransferPhoneNumber] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [activeTab, setActiveTab] = useState("deposit");

  const [isDepositing, setIsDepositing] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const t = useCallback(
    (key, variables = {}) => {
      let translation =
        translations[language]?.[key] || translations.en[key] || key;
      for (const [placeholder, value] of Object.entries(variables)) {
        translation = translation.replace(`{${placeholder}}`, value);
      }
      return translation;
    },
    [language]
  );

  const showToast = useCallback((message, type) => {
    toast[type](message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "dark",
    });
  }, []);

  // All supported payment methods for deposit (Chapa supported banks)
  const depositPaymentMethods = useMemo(
    () => [
      { value: "tele_birr_ussd", label: t("telebirr") },
      { value: "m_pesa", label: t("mPesa") },
      { value: "cbe_birr", label: t("cbeBirrOption") },
      { value: "amole", label: t("amole") },
      { value: "e_birr", label: t("eBirr") },
      { value: "coopay_ebirr", label: t("coopay") },
      { value: "awash_birr", label: t("awashBirr") },
      { value: "yaya", label: t("yaya") },
      { value: "enat_bank", label: t("enatBank") },
    ],
    [t]
  );

  // Withdrawal payment methods (only those supported for payouts)
  const withdrawPaymentMethods = useMemo(
    () => [
      { value: "tele_birr_ussd", label: t("telebirr") },
      { value: "m_pesa", label: t("mPesa") },
      { value: "cbe_birr", label: t("cbeBirrOption") },
      { value: "amole", label: t("amole") },
      { value: "e_birr", label: t("eBirr") },
      { value: "awash_birr", label: t("awashBirr") },
      { value: "yaya", label: t("yaya") },
    ],
    [t]
  );

  const normalizePhoneNumber = useCallback((phone) => {
    if (!phone) return null;
    phone = phone.trim();

    if (phone.startsWith("+")) {
      phone = phone.slice(1);
    }

    if (/^09\d{8}$/.test(phone)) {
      phone = "251" + phone.slice(1);
    } else if (/^07\d{8}$/.test(phone)) {
      phone = "251" + phone.slice(1);
    } else if (/^2519\d{8}$/.test(phone) || /^2517\d{8}$/.test(phone)) {
    } else {
      return null;
    }
    return phone;
  }, []);

  // Poll for payment status
  const pollPaymentStatus = useCallback(async (txRef, maxAttempts = 30, interval = 3000) => {
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, interval));
      
      try {
        const response = await axiosInstance.get(`/user/verify-payment/${txRef}`);
        
        if (response.data.success) {
          if (response.data.status === 'success') {
            return { success: true, balance: response.data.balance };
          } else if (response.data.status === 'failed') {
            return { success: false, failed: true };
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }
    return { success: false, timeout: true };
  }, []);

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/user/profile");
      if (response.data.success) {
        setUserData({
          ...response.data.user,
          depositBalance: parseFloat(response.data.user.depositBalance) || 0.0,
          bonusBalance: parseFloat(response.data.user.bonusBalance) || 0.0,
        });
        setError("");
      } else {
        showToast(response.data.message || t("failedToFetchUser"), "error");
        setError(t("error"));
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        showToast(t("sessionExpired"), "error");
        setError(t("sessionExpired"));
        localStorage.removeItem("token");
        navigate("/login");
      } else {
        showToast(
          err.response?.data?.message || t("networkOrServerError"),
          "error"
        );
        setError(t("networkOrServerError"));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, t, showToast]);

  const fetchTransactionHistory = useCallback(async () => {
    try {
      setLoadingTransactions(true);
      const response = await axiosInstance.get("/user/transactions");
      if (response.data.success) {
        const processedTransactions = response.data.transactions.map((tx) => ({
          ...tx,
          amount: parseFloat(tx.amount).toFixed(2),
          created_at_formatted: new Date(tx.created_at).toLocaleString(),
          bank_type: tx.bank_type || "--",
        }));
        setTransactions(processedTransactions);
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        showToast(t("sessionExpired"), "error");
        localStorage.removeItem("token");
        navigate("/login");
      } else {
        showToast(t("networkOrServerError"), "error");
      }
    } finally {
      setLoadingTransactions(false);
    }
  }, [navigate, t, showToast]);

  const fetchWithdrawalRequests = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/user/wallet/withdrawal-requests");
      if (response.data.success) {
        setWithdrawalRequests(response.data.withdrawalRequests);
      }
    } catch (err) {
      console.error('Failed to fetch withdrawal requests:', err);
    }
  }, []);

  const handleInitiateDeposit = async () => {
    const amountValue = parseFloat(depositAmount);
    if (isNaN(amountValue) || amountValue <= 0) {
      showToast(t("validAmount"), "error");
      return;
    }

    if (amountValue < MIN_DEPOSIT) {
      showToast(t("minDeposit"), "error");
      return;
    }

    if (amountValue > MAX_DEPOSIT) {
      showToast(t("maxDeposit"), "error");
      return;
    }

    if (!selectedDepositBank) {
      showToast(t("selectPaymentMethod"), "error");
      return;
    }

    setIsDepositing(true);
    let txRef = null;
    
    try {
      const response = await axiosInstance.post("/user/wallet/deposit", {
        amount: amountValue,
        bank: selectedDepositBank,
      });

      if (response.data.success) {
        txRef = response.data.externalTransactionId;
        const bankDisplayName = response.data.bankDisplayName;
        
        showToast(
          `${t("depositInitiated")}`,
          "info"
        );

        // Start polling for payment status
        setCheckingPayment(true);
        const result = await pollPaymentStatus(txRef);
        setCheckingPayment(false);

        if (result.success) {
          showToast(t("paymentSuccessful"), "success");
          await fetchUserData();
          await fetchTransactionHistory();
          setDepositAmount("");
          setSelectedDepositBank("");
          
          // Show updated balance
          if (result.balance !== undefined) {
            showToast(`${t("depositSuccessful")} ${t("birr")} ${result.balance.toFixed(2)}`, "success");
          }
        } else if (result.failed) {
          showToast(t("paymentFailed"), "error");
        } else if (result.timeout) {
          showToast(t("paymentTimeout"), "warning");
        }
      } else {
        showToast(response.data.message || t("depositFailed"), "error");
      }
    } catch (err) {
      setCheckingPayment(false);
      showToast(
        err.response?.data?.message || t("networkOrServerError"),
        "error"
      );
    } finally {
      setIsDepositing(false);
    }
  };

  const handleWithdraw = async () => {
    const amountValue = parseFloat(withdrawAmount);
    if (isNaN(amountValue) || amountValue <= 0) {
      showToast(t("validAmount"), "error");
      return;
    }

    if (amountValue < MIN_WITHDRAWAL) {
      showToast(t("minWithdrawal"), "error");
      return;
    }

    if (amountValue > MAX_WITHDRAWAL) {
      showToast(t("maxWithdrawal"), "error");
      return;
    }

    if (!selectedWithdrawBank) {
      showToast(t("selectPaymentMethod"), "error");
      return;
    }

    if (
      userData &&
      userData.depositBalance - amountValue < MIN_BALANCE_AFTER_WITHDRAWAL
    ) {
      showToast(t("insufficientBalanceToMaintainMinimum"), "error");
      return;
    }

    setIsWithdrawing(true);

    try {
      const response = await axiosInstance.post("/user/wallet/withdraw", {
        amount: amountValue,
        bank: selectedWithdrawBank,
      });

      if (response.data.success) {
        showToast(t("withdrawalSuccessful"), "success");
        await fetchUserData();
        await fetchTransactionHistory();
        await fetchWithdrawalRequests();
        setWithdrawAmount("");
        setSelectedWithdrawBank("");
      } else {
        showToast(response.data.message || t("withdrawalFailed"), "error");
      }
    } catch (err) {
      showToast(
        err.response?.data?.message || t("networkOrServerError"),
        "error"
      );
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleTransfer = async () => {
    const amountValue = parseFloat(transferAmount);
    if (isNaN(amountValue) || amountValue <= 0) {
      showToast(t("validAmount"), "error");
      return;
    }
    
    if (amountValue < MIN_TRANSFER) {
      showToast(t("minTransfer"), "error");
      return;
    }
    
    if (amountValue > MAX_TRANSFER) {
      showToast(t("maxTransfer"), "error");
      return;
    }
    
    const normalizedRecipientPhone = normalizePhoneNumber(transferPhoneNumber);
    if (!normalizedRecipientPhone) {
      showToast(t("invalidPhoneNumber"), "error");
      return;
    }
    
    if (userData && amountValue > userData.depositBalance) {
      showToast(t("insufficientBalance"), "error");
      return;
    }

    setIsTransferring(true);
    try {
      const response = await axiosInstance.post("/user/wallet/transfer", {
        recipientPhoneNumber: normalizedRecipientPhone,
        amount: amountValue,
      });

      if (response.data.success) {
        showToast(t("transferSuccessful"), "success");
        setTransferPhoneNumber("");
        setTransferAmount("");
        await fetchUserData();
        await fetchTransactionHistory();
      } else {
        showToast(response.data.message || t("transferFailed"), "error");
      }
    } catch (err) {
      if (err.response?.data?.message === "You cannot transfer to yourself") {
        showToast(t("cannotTransferToSelf"), "error");
      } else {
        showToast(
          err.response?.data?.message || t("networkOrServerError"),
          "error"
        );
      }
    } finally {
      setIsTransferring(false);
    }
  };

  const getTabClass = (tabName) =>
    `flex-1 py-3 px-2 text-center flex flex-col items-center justify-center font-bold rounded-lg transition-colors duration-200 cursor-pointer select-none ${
      activeTab === tabName
        ? "bg-blue-700 text-white shadow-lg"
        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
    }`;

  useEffect(() => {
    setCurrentPage(t("myWallet"));
    fetchUserData();
    fetchWithdrawalRequests();
  }, [setCurrentPage, fetchUserData, fetchWithdrawalRequests, t]);

  useEffect(() => {
    if (activeTab === "history") {
      fetchTransactionHistory();
    }
  }, [activeTab, fetchTransactionHistory]);

  if (loading) {
    return (
      <PageWrapper {...props}>
        <div className="flex justify-center items-center h-screen">
          <p className="text-gray-700 text-lg">{t("loadingWallet")}</p>
        </div>
      </PageWrapper>
    );
  }

  if (error && (error === t("sessionExpired") || error === t("networkError"))) {
    return (
      <PageWrapper {...props}>
        <div className="flex flex-col justify-center items-center h-screen text-red-600">
          <p className="text-lg mb-2">
            {t("error")}: {error}
          </p>
          <button
            onClick={() => navigate("/login")}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md"
          >
            {t("goToLogin")}
          </button>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      {...props}
      backgroundClass="bg-gradient-to-br from-purple-800 to-indigo-900 text-white min-h-screen"
    >
      <ToastContainer />
      <div className="relative max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-center mb-8 flex items-center justify-center space-x-3">
          <WalletIcon className="w-8 h-8 text-yellow-400" />
          <span>{t("myWallet")}</span>
        </h1>

        <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-xl p-6 mb-8 border border-white border-opacity-20">
          <div className="flex flex-col sm:flex-row items-center justify-around text-center sm:text-left space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-3">
              <div>
                <p className="text-sm text-gray-300">{t("depositBalance")}</p>
                <p className="text-3xl font-extrabold text-white">
                  {userData?.depositBalance !== undefined
                    ? userData.depositBalance.toFixed(2)
                    : t("na")}{" "}
                  {t("birr")}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div>
                <p className="text-sm text-gray-300">{t("bonusBalance")}</p>
                <p className="text-3xl font-extrabold text-white">
                  {userData?.bonusBalance !== undefined
                    ? userData.bonusBalance.toFixed(2)
                    : t("na")}{" "}
                  {t("birr")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Status indicator for payment checking */}
        {checkingPayment && (
          <div className="bg-yellow-500 bg-opacity-20 backdrop-blur-sm rounded-xl p-4 mb-6 border border-yellow-400 border-opacity-30 text-center">
            <div className="animate-spin inline-block w-5 h-5 border-2 border-yellow-400 border-t-transparent rounded-full mr-2"></div>
            <span className="text-yellow-200">{t("checkingPayment")}</span>
            <p className="text-xs text-yellow-300 mt-1">{t("pleaseCheckPhone")}</p>
          </div>
        )}

        <div className="grid grid-cols-4 gap-2 mb-8 p-1 bg-gray-800 rounded-lg shadow-inner">
          <div
            onClick={() => setActiveTab("deposit")}
            className={getTabClass("deposit")}
          >
            <PlusCircleIcon className="w-6 h-6 mb-1" />
            <span>{t("deposit")}</span>
          </div>
          <div
            onClick={() => setActiveTab("withdraw")}
            className={getTabClass("withdraw")}
          >
            <MinusCircleIcon className="w-6 h-6 mb-1" />
            <span>{t("withdraw")}</span>
          </div>
          <div
            onClick={() => setActiveTab("transfer")}
            className={getTabClass("transfer")}
          >
            <SendIcon className="w-6 h-6 mb-1" />
            <span>{t("transfer")}</span>
          </div>
          <div
            onClick={() => setActiveTab("history")}
            className={getTabClass("history")}
          >
            <HistoryIcon className="w-6 h-6 mb-1" />
            <span>{t("history")}</span>
          </div>
        </div>

        {activeTab === "deposit" && (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-xl p-6 border border-white border-opacity-20 flex flex-col items-center mb-6">
            <PlusCircleIcon className="w-12 h-12 text-green-400 mb-4" />
            <h2 className="text-2xl font-bold mb-4">{t("depositFunds")}</h2>

            <input
              type="number"
              value={depositAmount}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "") {
                  setDepositAmount("");
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue)) {
                    if (numValue < MIN_DEPOSIT) {
                      setDepositAmount(value);
                    } else if (numValue > MAX_DEPOSIT) {
                      setDepositAmount(MAX_DEPOSIT.toString());
                    } else {
                      setDepositAmount(value);
                    }
                  }
                }
              }}
              placeholder={t("depositAmountPlaceholder")}
              className="w-full p-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400 mb-4"
              min={MIN_DEPOSIT}
              max={MAX_DEPOSIT}
              step="10"
            />

            <select
              value={selectedDepositBank}
              onChange={(e) => setSelectedDepositBank(e.target.value)}
              className="w-full p-3 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-green-400 mb-4"
            >
              <option value="">{t("selectPaymentMethod")}</option>
              {depositPaymentMethods.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>

            <button
              onClick={handleInitiateDeposit}
              disabled={isDepositing || checkingPayment}
              className={`w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg transition-colors shadow-lg flex items-center justify-center gap-2 mb-3 ${
                (isDepositing || checkingPayment) ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {(isDepositing || checkingPayment) ? (
                <>
                  <span className="animate-spin">↻</span>
                  <span>{checkingPayment ? t("checkingPayment") : t("deposit")}</span>
                </>
              ) : (
                <>
                  <span className="text-yellow-300 text-xl">★</span>
                  {t("deposit")}
                </>
              )}
            </button>
          </div>
        )}

        {activeTab === "withdraw" && (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-xl p-6 border border-white border-opacity-20 flex flex-col items-center max-w-md mx-auto">
            <MinusCircleIcon className="w-12 h-12 text-red-400 mb-4" />
            <h2 className="text-2xl font-bold mb-4 text-center">
              {t("directWithdraw")}
            </h2>
            <input
              type="number"
              value={withdrawAmount}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "") {
                  setWithdrawAmount("");
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue)) {
                    if (numValue < MIN_WITHDRAWAL) {
                      setWithdrawAmount(value);
                    } else if (numValue > MAX_WITHDRAWAL) {
                      setWithdrawAmount(MAX_WITHDRAWAL.toString());
                    } else {
                      setWithdrawAmount(value);
                    }
                  }
                }
              }}
              placeholder={t("enterAmountPlaceholder")}
              className="w-full p-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400 mb-4"
              min={MIN_WITHDRAWAL}
              max={MAX_WITHDRAWAL}
              step="10"
            />
            <div className="mb-4 w-full">
              <label className="block text-gray-300 text-sm font-semibold mb-2">
                {t("selectPaymentMethod")}
              </label>
              <select
                value={selectedWithdrawBank}
                onChange={(e) => setSelectedWithdrawBank(e.target.value)}
                className="w-full p-3 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                <option value="">{t("selectPaymentMethod")}</option>
                {withdrawPaymentMethods.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleWithdraw}
              disabled={isWithdrawing}
              className={`w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-colors shadow-lg flex items-center justify-center gap-2 ${
                isWithdrawing ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isWithdrawing ? (
                <>
                  <span className="animate-spin">↻</span>
                  <span>{t("processingWithdrawal")}</span>
                </>
              ) : (
                t("withdrawCash")
              )}
            </button>
          </div>
        )}

        {activeTab === "transfer" && (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-xl p-6 border border-white border-opacity-20 flex flex-col items-center max-w-md mx-auto">
            <SendIcon className="w-12 h-12 text-blue-400 mb-4" />
            <h2 className="text-2xl font-bold mb-4 text-center">
              {t("transferFunds")}
            </h2>

            <input
              type="text"
              value={transferPhoneNumber}
              onChange={(e) => setTransferPhoneNumber(e.target.value)}
              placeholder={t("recipientPhoneNumber")}
              className="w-full p-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-4"
            />
            <input
              type="number"
              value={transferAmount}
              onChange={(e) => setTransferAmount(e.target.value)}
              placeholder={t("amountToTransfer")}
              className="w-full p-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-4"
              min={MIN_TRANSFER}
              max={MAX_TRANSFER}
              step="10"
            />

            <button
              onClick={handleTransfer}
              disabled={isTransferring}
              className={`w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors shadow-lg flex items-center justify-center gap-2 ${
                isTransferring ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isTransferring ? (
                <>
                  <span className="animate-spin">↻</span>
                  <span>{t("processingTransfer")}</span>
                </>
              ) : (
                t("confirmTransfer")
              )}
            </button>
          </div>
        )}

        {activeTab === "history" && (
          <div className="grid grid-cols-1 gap-6">
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-xl p-6 border border-white border-opacity-20 overflow-hidden">
              <h2 className="text-2xl font-bold mb-4 flex items-center space-x-2">
                <DollarSignIcon className="w-6 h-6 text-green-400" />
                <span>{t("transactionHistory")}</span>
              </h2>
              {loadingTransactions ? (
                <p className="text-gray-300">{t("loadingTransactions")}</p>
              ) : transactions.length === 0 ? (
                <p className="text-gray-300">{t("noTransactions")}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead className="bg-gray-700 bg-opacity-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                          {t("type")}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                          {t("amount")}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                          {t("bank")}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                          {t("status")}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                          {t("date")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {transactions.map((tx) => (
                        <tr
                          key={tx.id}
                          className="hover:bg-gray-700 hover:bg-opacity-20"
                        >
                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                tx.type === "deposit"
                                  ? "bg-green-100 text-green-800"
                                  : tx.type === "withdrawal"
                                  ? "bg-red-100 text-red-800"
                                  : tx.type === "win"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {tx.type === "deposit" ? t("deposit") : 
                               tx.type === "withdrawal" ? t("withdraw") : 
                               tx.type === "win" ? "Win" : 
                               tx.type === "transfer_sent" ? "Transfer Sent" :
                               tx.type === "transfer_received" ? "Transfer Received" : tx.type}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-200">
                            {Math.abs(parseFloat(tx.amount)).toFixed(2)} {t("birr")}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-200">
                            {tx.bank_type || "--"}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                tx.status === "success"
                                  ? "bg-green-100 text-green-800"
                                  : tx.status === "pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : tx.status === "failed"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {t(tx.status || "na")}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-200">
                            {tx.created_at_formatted}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Withdrawal Requests Section */}
            {withdrawalRequests.length > 0 && (
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-xl p-6 border border-white border-opacity-20 overflow-hidden">
                <h2 className="text-2xl font-bold mb-4 flex items-center space-x-2">
                  <MinusCircleIcon className="w-6 h-6 text-red-400" />
                  <span>{t("withdrawRequests")}</span>
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead className="bg-gray-700 bg-opacity-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                          {t("requestId")}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                          {t("amount")}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                          {t("bank")}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                          {t("statusLabel")}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                          {t("requestedOn")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {withdrawalRequests.map((req) => (
                        <tr key={req.id} className="hover:bg-gray-700 hover:bg-opacity-20">
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-200">
                            #{req.id}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-200">
                            {parseFloat(req.amount).toFixed(2)} {t("birr")}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-200">
                            {req.bank_type || "--"}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                req.status === "approved"
                                  ? "bg-green-100 text-green-800"
                                  : req.status === "completed"
                                  ? "bg-blue-100 text-blue-800"
                                  : req.status === "rejected"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {t(req.status)}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-200">
                            {new Date(req.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

export default WalletPage;